<template>
  <transition name="info-fade">
    <ModalContainer v-if="isOpen" @click="onBackdrop">
      <div
        ref="dialog"
        class="info-modal"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="`info-modal-title-${current.id}`"
      >
        <section class="card">
          <transition :name="`info-slide-${direction}`" mode="out-in">
            <div :key="current.id" class="slide">
              <h2 :id="`info-modal-title-${current.id}`" class="title">
                {{ current.title }}
              </h2>
              <div
                ref="body"
                class="body"
                v-html="current.description"
                @pointerdown="onPointerDown"
                @pointerup="onPointerUp"
              ></div>
            </div>
          </transition>

          <div v-if="hasMany" class="pager">
            <button
              v-for="(notice, i) in notices"
              :key="notice.id"
              type="button"
              class="dot"
              :class="{ active: i === index }"
              :aria-label="`${i + 1} / ${notices.length}`"
              :aria-current="i === index"
              @click="goTo(i)"
            ></button>
            <span class="counter">{{ index + 1 }} / {{ notices.length }}</span>
          </div>

          <button type="button" class="close" @click="close">
            {{ $t("buttons.close") }}
          </button>
        </section>

        <template v-if="hasMany">
          <button
            type="button"
            class="arrow prev"
            :aria-label="$t('infoModal.prev')"
            @click="prev"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <button
            type="button"
            class="arrow next"
            :aria-label="$t('infoModal.next')"
            @click="next"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </template>
      </div>
    </ModalContainer>
  </transition>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ModalContainer from "@/components/ModalContainer.vue";
import { getInfoModals } from "@/network/v1";

const SWIPE_THRESHOLD = 50;

const { locale } = useI18n({ useScope: "global" });
const notices = ref([]);
const index = ref(0);
const direction = ref("next");
const isClosed = ref(false);
const dialog = ref(null);
const body = ref(null);

const isOpen = computed(() => !isClosed.value && notices.value.length > 0);
const hasMany = computed(() => notices.value.length > 1);
const current = computed(() => notices.value[index.value] || {});

function load() {
  getInfoModals((res) => {
    notices.value = Array.isArray(res) ? res : [];
    if (index.value >= notices.value.length) index.value = 0;
  });
}

function goTo(i) {
  const count = notices.value.length;
  if (!count || i === index.value) return;
  direction.value = i > index.value ? "next" : "prev";
  index.value = (i + count) % count;
}

function next() {
  direction.value = "next";
  index.value = (index.value + 1) % notices.value.length;
}

function prev() {
  direction.value = "prev";
  index.value = (index.value - 1 + notices.value.length) % notices.value.length;
}

function close() {
  isClosed.value = true;
}

function onBackdrop(e) {
  if (dialog.value && !dialog.value.contains(e.target)) close();
}

function onKeydown(e) {
  if (!isOpen.value) return;
  if (e.key === "Escape") close();
  else if (hasMany.value && e.key === "ArrowRight") next();
  else if (hasMany.value && e.key === "ArrowLeft") prev();
}

let swipeStart = null;
function onPointerDown(e) {
  swipeStart = { x: e.clientX, y: e.clientY };
}
function onPointerUp(e) {
  if (!swipeStart || !hasMany.value) return;
  const dx = e.clientX - swipeStart.x;
  const dy = e.clientY - swipeStart.y;
  swipeStart = null;
  if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
  if (dx < 0) next();
  else prev();
}

watch(isOpen, (open) => {
  document.body.style.overflow = open ? "hidden" : "";
});
watch(index, () => {
  if (body.value) body.value.scrollTop = 0;
});
watch(locale, load);

onMounted(() => {
  load();
  window.addEventListener("keydown", onKeydown);
});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  document.body.style.overflow = "";
});
</script>

<style lang="scss" scoped>
.info-modal {
  position: relative;
  width: min(32rem, 100%);
  margin: 0 auto;
}

.card {
  display: flex;
  flex-direction: column;
  background: var(--white);
  color: var(--neutrals);
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1.5rem 3rem rgba(24, 33, 77, 0.25);
}

.slide {
  padding: 1.75rem 1.5rem 0;
}

.title {
  margin: 0;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--dark-blue);
  color: var(--dark-blue);
  font-size: 1.375rem;
  font-weight: 700;
  line-height: 1.35;
  text-align: center;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.body {
  max-height: min(50vh, 24rem);
  overflow-y: auto;
  padding: 1.25rem 0.5rem 1.25rem 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--neutrals);
  word-break: keep-all;
  overflow-wrap: anywhere;
  touch-action: pan-y;
  scrollbar-width: thin;
  scrollbar-color: var(--disabled-color) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--disabled-color);
    border-radius: 3px;
  }

  :deep(p) {
    margin: 0 0 0.75rem;
  }
  :deep(p:last-child) {
    margin-bottom: 0;
  }
  :deep(ul),
  :deep(ol) {
    margin: 0 0 0.75rem;
    padding-left: 1.25rem;
  }
  :deep(ul) {
    list-style: disc;
  }
  :deep(ol) {
    list-style: decimal;
  }
  :deep(strong) {
    font-weight: 700;
  }
  :deep(a) {
    color: var(--primary-blue);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  :deep(figure.table) {
    margin: 1rem 0;
    overflow-x: auto;
  }
  :deep(table) {
    width: 100%;
    margin: 1rem 0;
    border-collapse: collapse;
    border: 1px solid var(--dark-blue);
  }
  :deep(figure.table table) {
    margin: 0;
  }
  :deep(th),
  :deep(td) {
    padding: 0.6rem 0.75rem;
    border: 1px solid var(--dark-blue);
    text-align: center;
    vertical-align: middle;
  }
  :deep(th),
  :deep(thead td) {
    background: var(--bg-light-grey);
    font-weight: 600;
  }
  :deep(img) {
    max-width: 100%;
    height: auto;
  }
}

.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0 1.5rem 1rem;
}

.dot {
  width: 0.5rem;
  height: 0.5rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: var(--light-gray);
  cursor: pointer;
  transition: width 0.2s, background-color 0.2s;

  &.active {
    width: 1.25rem;
    background: var(--dark-blue);
  }
}

.counter {
  margin-left: 0.4rem;
  font-size: 0.8rem;
  color: var(--neutrals60);
  font-variant-numeric: tabular-nums;
}

.close {
  width: 100%;
  padding: 1rem;
  border: 0;
  background: var(--dark-blue);
  color: var(--white);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover,
  &:focus-visible {
    background: var(--neutrals);
  }
}

.arrow {
  position: absolute;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: 1px solid var(--border-light);
  border-radius: 50%;
  background: var(--white);
  box-shadow: 0 0.25rem 0.75rem rgba(24, 33, 77, 0.2);
  cursor: pointer;
  transform: translateY(-50%);
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-50%) scale(1.08);
  }

  &.prev {
    left: -1.25rem;
  }
  &.next {
    right: -1.25rem;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
    fill: none;
    stroke: var(--dark-blue);
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  @media screen and (max-width: 576px) {
    width: 2.25rem;
    height: 2.25rem;
    &.prev {
      left: -1rem;
    }
    &.next {
      right: -1rem;
    }
  }
}

button:focus-visible {
  outline: 2px solid var(--primary-blue);
  outline-offset: 2px;
}

.info-fade-enter-active,
.info-fade-leave-active {
  transition: opacity 0.25s;
  .card {
    transition: transform 0.25s;
  }
}
.info-fade-enter-from,
.info-fade-leave-to {
  opacity: 0;
  .card {
    transform: scale(0.96);
  }
}

.info-slide-next-enter-active,
.info-slide-next-leave-active,
.info-slide-prev-enter-active,
.info-slide-prev-leave-active {
  transition: opacity 0.18s, transform 0.18s;
}
.info-slide-next-enter-from,
.info-slide-prev-leave-to {
  opacity: 0;
  transform: translateX(1.5rem);
}
.info-slide-next-leave-to,
.info-slide-prev-enter-from {
  opacity: 0;
  transform: translateX(-1.5rem);
}

@media (prefers-reduced-motion: reduce) {
  .info-slide-next-enter-active,
  .info-slide-next-leave-active,
  .info-slide-prev-enter-active,
  .info-slide-prev-leave-active,
  .info-fade-enter-active,
  .info-fade-leave-active {
    transition: none;
  }
}
</style>
